/*
 * cluutch-drawer-recos.js
 * CRO wave 2 (PDP-M) — recommandations du tiroir panier, juste après un ajout.
 *
 * Pourquoi ne pas réutiliser <product-recommendations> (assets/product-recommendations.js) :
 * il lance son fetch dans connectedCallback et n'expose aucune prise pour la fin, donc
 * impossible d'enchaîner proprement un second appel. Or ce tiroir a besoin d'une règle
 * que le natif n'a pas : demander d'abord les produits COMPLÉMENTAIRES configurés dans
 * Search & Discovery, et ne retomber sur l'algorithme générique que s'il n'y en a aucun.
 * Ce repli serait au contraire un défaut sur l'encart « existe aussi en kit », qui doit
 * rester vide plutôt que d'afficher n'importe quoi : deux contrats, deux composants.
 *
 * Choix structurants :
 * - le contrat de classes du natif est reproduit à l'identique (--full, puis .active si
 *   le tiroir est déjà ouvert) : sans --full, app.js n'ouvrira JAMAIS la colonne latérale
 *   sur ordinateur, il ne pose .active que si elle est déjà là et la retire à la fermeture ;
 * - tout échec (réseau, réponse non-2xx, HTML inattendu) est absorbé : le panier doit
 *   rester utilisable sans recommandations. On ne relance qu'une fois, puis on se tait ;
 * - l'ajout en un geste n'est pas réimplémenté : les cartes sont des <product-card> avec
 *   le bouton natif .product-card--add-to-cart-button-simple, qu'app.js câble tout seul.
 *
 * Wave 2 (G4) — trois ajouts :
 * - le repli ne se déclenche plus seulement quand l'intention « complémentaires » est VIDE,
 *   mais dès qu'elle est trop maigre. Mesuré sur le mascara : Search & Discovery n'y a qu'un
 *   seul complémentaire configuré, donc le tiroir n'affichait qu'une carte. On complète avec
 *   la sélection générique, sans doublon, jusqu'à remplir la rangée. Ce n'est qu'un pansement
 *   sur une configuration marchande à faire (backlog K9) : le jour où elle sera faite, le
 *   complément ne se déclenchera plus de lui-même ;
 * - une MÉMOIRE des réponses. Le tiroir se re-rend à chaque changement de quantité, ce qui
 *   détruit puis recrée l'élément, donc relançait le fetch à chaque clic sur + ou −. Le cache
 *   vit au niveau du MODULE et non de l'instance, justement parce que l'instance ne survit
 *   pas ; il retient la PROMESSE et non son résultat, sinon deux rendus rapprochés partiraient
 *   tous les deux avant que le premier n'ait répondu. La clé est l'URL, elle change d'elle-même
 *   quand le premier article du panier change ;
 * - le bouton « Voir plus », rendu par la section (il arrive donc avec le HTML récupéré) et
 *   câblé ici après coup. Il ne fait que révéler des cartes déjà rendues : aucun appel réseau.
 * Style dans cluutch-drawer.css. Auteur : Cluutch / Studio Hogo.
 */
(function () {
	if (window.cluutchDrawerRecosReady) return;
	window.cluutchDrawerRecosReady = true;

	// En dessous de ce nombre de complémentaires, on complète avec le générique.
	var SEUIL = 4;
	// Plafond : trois cartes visibles, trois derrière « Voir plus ».
	var PLAFOND = 6;
	// Clé = URL appelée, valeur = la promesse de son HTML (ou de null si elle n'a rien donné).
	var memoire = new Map();

	class CluutchDrawerRecos extends HTMLElement {
		constructor() {
			super();
			this.parent = this.closest('.product-recommendations--parent');
		}

		connectedCallback() {
			this.charger();
		}

		charger() {
			var self = this;
			var principale = this.dataset.url;
			var repli = this.dataset.urlRepli;

			if (!principale) {
				// Hôtel Charly (10/10) : la liste est rendue par le tiroir lui-même (snippets/cart-drawer.liquid,
				// les fiches non répertoriées étant exclues des recommandations de Shopify) ; on ouvre la colonne
				// comme après un fetch.
				if (this.querySelector('.cluutch-reco')) this.remplir(this.innerHTML);
				this.terminer();
				return;
			}

			this.recuperer(principale)
				.then(function (html) {
					// Trop peu de complémentaires configurés (souvent un seul, parfois aucun) :
					// on demande la sélection générique de Shopify et on complète avec.
					if (!repli || self.compter(html) >= SEUIL) return html;
					return self.recuperer(repli).then(function (autre) {
						return self.fusionner(html, autre);
					});
				})
				.then(function (html) {
					if (html) self.remplir(html);
				})
				.catch(function (e) {
					console.error(e);
				})
				.then(function () {
					self.terminer();
				});
		}

		recuperer(url) {
			var self = this;
			if (memoire.has(url)) return memoire.get(url);

			var promesse = fetch(url)
				.then(function (reponse) {
					if (!reponse.ok) throw new Error('recommandations panier : réponse ' + reponse.status);
					return reponse.text();
				})
				.then(function (texte) {
					var bloc = self.analyser(texte).querySelector(self.tagName.toLowerCase());
					var html = bloc ? bloc.innerHTML.trim() : '';
					return html.length ? html : null;
				})
				.catch(function (e) {
					// Un échec ne se mémorise pas : le prochain affichage du tiroir réessaiera.
					memoire.delete(url);
					throw e;
				});

			memoire.set(url, promesse);
			return promesse;
		}

		analyser(html) {
			var hote = document.createElement('div');
			hote.innerHTML = html || '';
			return hote;
		}

		compter(html) {
			return this.analyser(html).querySelectorAll('.cluutch-reco').length;
		}

		// Complète la première liste avec les cartes de la seconde, en écartant les produits
		// déjà présents. On compare les handles : le générique et les complémentaires
		// n'écrivent pas forcément la même adresse pour un même produit.
		fusionner(html, autre) {
			if (!html) return autre;
			if (!autre) return html;

			var base = this.analyser(html);
			var liste = base.querySelector('.cart-drawer--recommendations--products');
			if (!liste) return html;

			var vus = {};
			var handle = function (carte) {
				var lien = carte.querySelector('.cluutch-reco__nom');
				var href = lien ? lien.getAttribute('href') : '';
				return href.split('/products/')[1] ? href.split('/products/')[1].split('?')[0] : '';
			};

			[].forEach.call(liste.children, function (c) {
				vus[handle(c)] = true;
			});

			var candidates = this.analyser(autre).querySelectorAll('.cluutch-reco');
			for (var i = 0; i < candidates.length && liste.children.length < PLAFOND; i++) {
				var h = handle(candidates[i]);
				if (!h || vus[h]) continue;
				vus[h] = true;
				liste.appendChild(candidates[i]);
			}

			return base.innerHTML;
		}

		remplir(html) {
			this.innerHTML = html;
			this.cablerVoirPlus();
			if (!this.parent) return;
			this.parent.classList.add('product-recommendations--full');
			if (document.body.classList.contains('open-cart')) {
				this.parent.classList.add('active');
			}
		}

		// Le bouton révèle les cartes au-delà de la troisième, déjà rendues et seulement
		// masquées en CSS, puis s'efface : rien à recharger, rien à demander au serveur.
		// C'est ici, et non en Liquid, qu'on décide s'il a lieu d'être : le nombre final de
		// cartes n'est connu qu'après une éventuelle fusion.
		cablerVoirPlus() {
			var bouton = this.querySelector('.cluutch-reco-plus');
			var liste = this.querySelector('.cart-drawer--recommendations--products');
			if (!bouton) return;
			if (!liste || liste.children.length <= 3) {
				bouton.remove();
				return;
			}

			bouton.addEventListener('click', function () {
				liste.classList.add('cluutch-reco-liste--ouverte');
				bouton.remove();
			});
		}

		terminer() {
			this.classList.add('product-recommendations--loaded');
		}
	}

	if (!customElements.get('cluutch-drawer-recos')) {
		customElements.define('cluutch-drawer-recos', CluutchDrawerRecos);
	}
})();
